import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useTenant } from './TenantContext';
import { api } from '@/lib/api';

interface Project {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color: string;
  logoUrl?: string | null;
  defaultPipelineId?: string;
  createdAt: string;
}

// Speciální hodnota pro "Všechny projekty"
export const ALL_PROJECTS_ID = '__all__';

interface ProjectContextType {
  projects: Project[];
  currentProject: Project | null;
  currentProjectId: string | null; // null = všechny projekty
  isAllProjects: boolean;
  isLoading: boolean;
  switchProject: (projectId: string) => void;
  refreshProjects: () => Promise<void>;
  getProjectById: (id: string) => Project | undefined;
  getProjectColor: (id: string) => string;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const PROJECT_STORAGE_KEY = 'realvisor_current_project';

// Výchozí barvy pro projekty
const DEFAULT_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#8B5CF6', // purple
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#84CC16', // lime
];

export function ProjectProvider({ children }: { children: ReactNode }) {
  const { currentTenant } = useTenant();
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (currentTenant) {
      loadProjects();
    } else {
      setProjects([]);
      setCurrentProject(null);
      setIsLoading(false);
    }
  }, [currentTenant]);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<Project[]>('/projects');
      setProjects(data);

      // Restore last selected project for this tenant
      const storageKey = `${PROJECT_STORAGE_KEY}_${currentTenant?.id}`;
      const storedProjectId = localStorage.getItem(storageKey);
      
      // Pokud je uloženo ALL_PROJECTS_ID nebo neexistující projekt, nastavíme "Všechny"
      if (storedProjectId === ALL_PROJECTS_ID) {
        setSelectedProjectId(null);
        setCurrentProject(null);
      } else {
        const storedProject = data.find((p) => p.id === storedProjectId);
        if (storedProject) {
          setSelectedProjectId(storedProject.id);
          setCurrentProject(storedProject);
        } else {
          // Výchozí: "Všechny projekty"
          setSelectedProjectId(null);
          setCurrentProject(null);
        }
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchProject = (projectId: string) => {
    const storageKey = `${PROJECT_STORAGE_KEY}_${currentTenant?.id}`;
    
    if (projectId === ALL_PROJECTS_ID) {
      setSelectedProjectId(null);
      setCurrentProject(null);
      localStorage.setItem(storageKey, ALL_PROJECTS_ID);
    } else {
      const project = projects.find((p) => p.id === projectId);
      if (project && currentTenant) {
        setSelectedProjectId(project.id);
        setCurrentProject(project);
        localStorage.setItem(storageKey, project.id);
      }
    }
  };

  const refreshProjects = async () => {
    await loadProjects();
  };

  const getProjectById = (id: string): Project | undefined => {
    return projects.find((p) => p.id === id);
  };

  const getProjectColor = (id: string): string => {
    const project = projects.find((p) => p.id === id);
    if (project?.color) return project.color;
    // Fallback: přiřadit barvu podle indexu
    const index = projects.findIndex((p) => p.id === id);
    return DEFAULT_COLORS[index % DEFAULT_COLORS.length] || '#6B7280';
  };

  const isAllProjects = selectedProjectId === null;
  const currentProjectId = selectedProjectId;

  return (
    <ProjectContext.Provider
      value={{
        projects,
        currentProject,
        currentProjectId,
        isAllProjects,
        isLoading,
        switchProject,
        refreshProjects,
        getProjectById,
        getProjectColor,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}
